/**
 * Компактный селектор ветки/PR в TopBar'е.
 *
 * Группы в дропдауне:
 *   1. Открытые PR (если есть) — `#N Title` + ref снизу мелким моноспейсом
 *   2. Ветки — с пометкой `(default)` у default-ветки и дедупом same-repo
 *      ветки, у которой уже есть PR (она и так в группе PR'ов)
 *
 * Tags эндпоинт сервера пока не возвращает — секцию не показываем,
 * добавим, если расширим контракт.
 *
 * Лезет в `/refs` лениво — только когда дропдаун открыт первый раз.
 * До первого открытия показывает текущий `ref` (или 'main' по умолчанию)
 * без сетевого запроса.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Loader, Menu, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronDown, IconGitBranch, IconGitPullRequest } from '@tabler/icons-react';
import { fetchRefs, type RefsResponse } from '../lib/api-client';
import { DESKTOP_QUERY } from '../lib/breakpoints';

interface Props {
    owner:   string;
    repo:    string;
    current: string | undefined;          // undefined → default-ветка
    onChange: (ref: string | undefined) => void;
}

export function RefSelector({ owner, repo, current, onChange }: Props): JSX.Element {
    const [opened, setOpened] = useState(false);
    const isDesktop = useMediaQuery(DESKTOP_QUERY, true);
    const { data, isLoading, error } = useQuery({
        queryKey: ['refs', owner, repo],
        queryFn:  () => fetchRefs({ owner, repo }),
        enabled:  opened,         // ленивый запрос — до открытия меню не дёргаем
        staleTime: 30_000,
        retry:    false,
    });

    const display = current ?? 'main';

    return (
        <Menu
            opened={opened}
            onChange={setOpened}
            position="bottom-end"
            shadow="md"
            width={isDesktop ? 320 : 280}
            withinPortal
        >
            <Menu.Target>
                <Button
                    variant="default"
                    size="xs"
                    leftSection={<IconGitBranch size={14} />}
                    rightSection={<IconChevronDown size={12} />}
                    // На mobile сжимаем дисплей: 220px → 120px max-width.
                    style={{ fontFamily: 'monospace', maxWidth: isDesktop ? 220 : 120 }}
                >
                    <Text span fz="xs" truncate>{display}</Text>
                </Button>
            </Menu.Target>
            <Menu.Dropdown mah={520} style={{ overflowY: 'auto' }}>
                {isLoading && (
                    <Menu.Item disabled leftSection={<Loader size={12} />}>Загрузка…</Menu.Item>
                )}
                {error && (
                    <Menu.Item disabled>
                        <Text fz="xs" c="red">Ошибка: {(error as Error).message}</Text>
                    </Menu.Item>
                )}
                {data && <RefMenuContent
                    data={data}
                    current={current}
                    onChange={(v) => { onChange(v); setOpened(false); }}
                />}
            </Menu.Dropdown>
        </Menu>
    );
}

function RefMenuContent({ data, current, onChange }: {
    data: RefsResponse;
    current: string | undefined;
    onChange: (ref: string | undefined) => void;
}): JSX.Element {
    // Defensive: сервер может пропустить поля если что-то поломалось —
    // не падаем на .map(undefined). Не должно случаться, но cheap insurance.
    const branches = data.branches ?? [];
    const prs      = data.prs      ?? [];
    const def      = data.defaultBranch ?? 'main';

    // Дедуп: ветка same-repo, у которой есть открытый PR, не показывается
    // в списке Branches (она и так в группе PR'ов). Fork-PR'ы используют
    // refs/pull/N/head, не совпадающий с веткой — дедуп их не трогает.
    const branchesWithPr = new Set(prs.filter(p => !p.isFork).map(p => p.ref));
    const visibleBranches = branches.filter(b => !branchesWithPr.has(b));

    const isActive = (ref: string | undefined): boolean =>
        (current ?? def) === (ref ?? def);

    return (
        <>
            {prs.length > 0 && <>
                <Menu.Label>Открытые PR ({prs.length})</Menu.Label>
                {prs.map(pr => (
                    <Menu.Item
                        key={`pr-${pr.number}`}
                        leftSection={<IconGitPullRequest size={14} />}
                        onClick={() => onChange(pr.ref)}
                        bg={isActive(pr.ref) ? 'var(--mantine-color-default-hover)' : undefined}
                    >
                        <Text fz="xs" fw={500} truncate>#{pr.number} {pr.title}</Text>
                        <Text fz="xs" c="dimmed" ff="monospace" truncate>
                            {pr.isFork ? '⑂ ' : ''}{pr.ref}
                        </Text>
                    </Menu.Item>
                ))}
            </>}

            <Menu.Label>Ветки ({visibleBranches.length})</Menu.Label>
            {visibleBranches.map(b => (
                <Menu.Item
                    key={`br-${b}`}
                    leftSection={<IconGitBranch size={14} />}
                    onClick={() => onChange(b === def ? undefined : b)}
                    bg={isActive(b) ? 'var(--mantine-color-default-hover)' : undefined}
                >
                    <Text fz="xs" ff="monospace" truncate>
                        {b}{b === def && ' (default)'}
                    </Text>
                </Menu.Item>
            ))}

            {prs.length === 0 && visibleBranches.length === 0 && (
                <Menu.Item disabled>
                    <Text fz="xs" c="dimmed">Нет веток и PR'ов</Text>
                </Menu.Item>
            )}
        </>
    );
}
